import * as React from 'react';
import type { SortDir, SortKey } from '../units-tab/units-tab-toolbar';
import {
  EMPTY_FILTER_VALUE,
  type OfferTableRevealSurface,
  type UnitsTabFilterValue,
} from '../units-tab/units-tab-page';
import type { BulkDownloadManifestResponse, Client, Offer } from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ooh/ui/components/tabs';
import { OfferHeader, type OfferHeaderHandle } from '../header/offer-header';
import { OfferFinalizationSection } from '../header/offer-finalization-section';
import { isOfferReadOnly } from '../lib/workspace-access';
import { FeatureFlaggedTab } from './feature-flagged-tab';
import { UnitsTabPage } from '../units-tab/units-tab-page';
import { useNavigate } from '@tanstack/react-router';
import {
  ClientDecisionsTabPage,
  EditGateBanner,
  VersionHistoryTab,
  type UnitDecisionSortKey as DecisionSortKey,
} from '@ooh/screens';

// 'comments' tab is hidden until the offer-draft-comments-tab phase is activated.
// See openspec/changes/offer-draft-comments-tab/proposal.md (DEFERRED banner).
//
// The Dostępność and Wyceny tabs folded into the merged Nośniki table
// (`offer-unified-table` capability, CU-86cbc3r8b) — the workspace now
// offers exactly these three. `pricing`/`availability` remain valid `?tab=`
// VALUES (see `OFFER_WORKSPACE_TAB_REDIRECT_VALUES` in the route file) so a
// stored link redirects to `units` instead of 404ing, but they are no longer
// reachable tabs and render no `TabsTrigger`.
export const OFFER_WORKSPACE_TABS = ['units', 'versions', 'decisions'] as const;
export type OfferWorkspaceTab = (typeof OFFER_WORKSPACE_TABS)[number];

/**
 * Retired `?tab=` values that must still validate (so a stored bookmark
 * doesn't 404 or fall back to the default silently) but redirect to `units`
 * with replace semantics — see the route's `OfferWorkspaceSearchSchema` and
 * its `?tab=pricing`/`?tab=availability` redirect effect.
 */
export const OFFER_WORKSPACE_RETIRED_TAB_VALUES = ['pricing', 'availability'] as const;
export type OfferWorkspaceRetiredTabValue = (typeof OFFER_WORKSPACE_RETIRED_TAB_VALUES)[number];

/**
 * Pixel height of the sticky tabs bar below (`h-11`/`top-11` in its own className —
 * Tailwind classes can't reference a JS constant, so this is exported purely for
 * per-tab sticky-offset pixel math, e.g. units-tab-page.tsx's own sticky toolbar).
 */
export const WORKSPACE_TABS_BAR_HEIGHT_PX = 44;

interface WorkspaceShellReadyProps {
  activeTab: OfferWorkspaceTab;
  canAdminChangeStatus?: boolean;
  canEdit?: boolean;
  canConfirmCampaign?: boolean;
  canRecordSale?: boolean;
  /** Whether the offer's accepted nośniki still hold an unsecured campaign
   * (OfferDetail's `hasUnsecuredAcceptedUnits`) — drives the finalization
   * panel's secure-remaining alert. */
  hasUnsecuredUnits?: boolean;
  /** Whether the caller's `auth.permissions` has finished resolving — see
   * `OfferFinalizationSectionProps.isAccessResolved`. */
  isAccessResolved?: boolean;
  /** `reservation.manage` — threaded to the preliminary-reservation action
   * (offer-accept-reservation). */
  canManageReservation?: boolean;
  /** The offer's currently active `ACCEPTED` version — threaded to the
   * preliminary-reservation action the same way `canConfirmCampaign` flows
   * through this shell. */
  activeAcceptedVersionId?: number | null;
  activeAcceptedVersionNumber?: number | null;
  canManagePublicLink?: boolean;
  canManageSharing?: boolean;
  canTrash?: boolean;
  client?: Client;
  isForbidden?: false;
  offer: Offer;
  onTabChange: (tab: OfferWorkspaceTab) => void;
  unitsFilterValue?: UnitsTabFilterValue;
  unitsSearchValue?: string;
  /** Dedicated address text filter value (catalog parity, CU-86ca9k73a). */
  unitsAddressValue?: string;
  /** Numeric range dimensions (catalog parity, CU-86ca9k73a). */
  unitsRangeValue?: Record<string, string[]>;
  unitsSortKey: SortKey;
  unitsSortDir: SortDir;
  unitsDecisionVersionId?: number;
  /** Retired-address one-visit column reveal (offer-unified-table) — see `OfferTableRevealSurface`. */
  revealSurface?: OfferTableRevealSurface;
  decisionsSortKey?: DecisionSortKey;
  decisionsSortDir?: SortDir;
  onDecisionsSortChange?: (sortKey: DecisionSortKey, sortDir: SortDir) => void;
  onUnitsFilterChange?: (filterValue: UnitsTabFilterValue) => void;
  onUnitsSearchChange?: (search: string) => void;
  onUnitsAddressChange?: (address: string) => void;
  onUnitsRangeChange?: (rangeValue: Record<string, string[]>) => void;
  onUnitsSortChange: (sortKey: SortKey, sortDir: SortDir) => void;
  /** Resolved at route level (cost.view permission, CU-86ca9k73a) */
  canViewCosts?: boolean;
  /** Resolved at route level (wildcard cost.view_rent grant, CU-86cbgnt3z) */
  canViewRent?: boolean;
  /** Resolved at route level (supplier.view permission, catalog-column-filter-parity) */
  canViewSupplierDetails?: boolean;
  /** Wired from useBulkDownloadSpecs() at the route level */
  onBulkDownloadSpecs?: (ids: number[]) => void;
  bulkDownloadPendingManifest?: BulkDownloadManifestResponse | null;
  confirmBulkDownload?: () => Promise<void>;
  cancelBulkDownload?: () => void;
}

const EMPTY_UNITS_FILTER_VALUE: UnitsTabFilterValue = EMPTY_FILTER_VALUE;

interface WorkspaceShellForbiddenProps {
  activeTab?: OfferWorkspaceTab;
  client?: Client;
  isForbidden: true;
  offer?: Offer;
  onTabChange?: (tab: OfferWorkspaceTab) => void;
}

export type WorkspaceShellProps = WorkspaceShellReadyProps | WorkspaceShellForbiddenProps;

function getTabLabel(tab: OfferWorkspaceTab, t: ReturnType<typeof useTranslation<'offers'>>['t']) {
  switch (tab) {
    case 'units':
      return t('workspace.tabs.units');
    case 'versions':
      return t('workspace.tabs.versions');
    case 'decisions':
      return t('workspace.tabs.decisions');
  }
}

function WorkspaceForbiddenState() {
  const { t } = useTranslation('offers');

  return (
    <div
      className="flex min-h-[320px] items-center justify-center px-6 py-10 text-center"
      data-testid="offer-workspace-forbidden"
    >
      <p className="max-w-md text-sm text-muted-foreground">{t('workspace.forbidden')}</p>
    </div>
  );
}

function renderWorkspaceTabContent({
  activeTab,
  canEdit,
  offer,
  offerHeaderRef,
  onOpenVersion,
  props,
}: {
  activeTab: OfferWorkspaceTab;
  canEdit: boolean;
  offer: Offer;
  offerHeaderRef: React.RefObject<OfferHeaderHandle | null>;
  onOpenVersion: (versionId: number) => void;
  props: WorkspaceShellReadyProps;
}) {
  if (activeTab === 'units') {
    return (
      <div>
        <EditGateBanner offerId={String(offer.id)} offerStatus={offer.status} />
        <UnitsTabPage
          offerId={offer.id}
          canEdit={canEdit}
          filterValue={props.unitsFilterValue ?? EMPTY_UNITS_FILTER_VALUE}
          offerUpdatedAt={offer.updatedAt}
          onFilterChange={props.onUnitsFilterChange}
          search={props.unitsSearchValue}
          onSearchChange={props.onUnitsSearchChange}
          address={props.unitsAddressValue}
          onAddressChange={props.onUnitsAddressChange}
          rangeValue={props.unitsRangeValue}
          onRangeChange={props.onUnitsRangeChange}
          canViewCosts={props.canViewCosts}
          canViewRent={props.canViewRent}
          canViewSupplierDetails={props.canViewSupplierDetails}
          sortKey={props.unitsSortKey}
          sortDir={props.unitsSortDir}
          decisionVersionId={props.unitsDecisionVersionId}
          onSortChange={props.onUnitsSortChange}
          onBulkDownloadSpecs={props.onBulkDownloadSpecs}
          bulkDownloadPendingManifest={props.bulkDownloadPendingManifest}
          confirmBulkDownload={props.confirmBulkDownload}
          cancelBulkDownload={props.cancelBulkDownload}
          revealSurface={props.revealSurface}
          offerLabel={buildOfferIdentityLabel(offer, props.client)}
          defaultDateFrom={offer.defaultDateFrom}
          defaultDateTo={offer.defaultDateTo}
          onOpenCampaignDatesEditor={() => offerHeaderRef.current?.openEditDrawer()}
        />
      </div>
    );
  }

  if (activeTab === 'versions') {
    return (
      <VersionHistoryTab
        offerId={String(offer.id)}
        offer={offer}
        canEdit={canEdit}
        onOpenClientShare={() => offerHeaderRef.current?.openClientShareDialog()}
        onOpenVersion={onOpenVersion}
      />
    );
  }

  if (activeTab === 'decisions') {
    return (
      <ClientDecisionsTabPage
        offerId={String(offer.id)}
        canEdit={canEdit}
        canViewSupplierDetails={props.canViewSupplierDetails}
        sortKey={props.decisionsSortKey}
        sortDir={props.decisionsSortDir}
        onSortChange={props.onDecisionsSortChange}
      />
    );
  }

  return <FeatureFlaggedTab />;
}

function getWorkspaceTabTestId(tab: OfferWorkspaceTab) {
  if (tab === 'units') {
    return 'offer-units-tab';
  }

  return undefined;
}

function getWorkspaceTabTriggerTestId(tab: OfferWorkspaceTab): string | undefined {
  if (tab === 'versions') {
    return 'versions-tab';
  }

  if (tab === 'decisions') {
    return 'decisions-tab';
  }

  return undefined;
}

/**
 * Visible offer/client/brand identity summary for the availability tab's
 * queue-join panel (CU-86cb7ewja) — e.g. "Wakacyjna kampania 2026 · Orange
 * Polska S.A. · Orange Flex". `client` may be undefined while the client
 * query is still loading; the offer title alone is still a valid label.
 */
function buildOfferIdentityLabel(offer: Offer, client: Client | undefined): string {
  const parts = [offer.title];
  if (client?.name) parts.push(client.name);
  if (offer.brand) parts.push(offer.brand);
  return parts.join(' · ');
}

/** `value ?? false` as its own function so callers with many optional boolean
 * props (getWorkspaceHeaderProps) don't accumulate one cyclomatic-complexity
 * branch per prop. */
function defaultFalse(value: boolean | undefined): boolean {
  return value ?? false;
}

function getWorkspaceHeaderProps(
  props: WorkspaceShellReadyProps,
  canEdit: boolean,
  t: ReturnType<typeof useTranslation<'offers'>>['t'],
) {
  return {
    canAdminChangeStatus: defaultFalse(props.canAdminChangeStatus),
    canEdit,
    canManagePublicLink: defaultFalse(props.canManagePublicLink),
    canManageSharing: defaultFalse(props.canManageSharing),
    canTrash: defaultFalse(props.canTrash),
    clientBrands: props.client?.brands ?? [],
    clientName: props.client?.name ?? t('workspace.header.missingClient'),
  };
}

/** Finalization actions live only in `OfferFinalizationSection`, rendered as
 * a sibling of `OfferHeader` — never inside the header itself (offer-approval
 * / "Finalization path in the offer workspace"). */
function getFinalizationSectionProps(props: WorkspaceShellReadyProps, canEdit: boolean) {
  return {
    canConfirmCampaign: defaultFalse(props.canConfirmCampaign),
    canRecordSale: defaultFalse(props.canRecordSale),
    hasUnsecuredUnits: defaultFalse(props.hasUnsecuredUnits),
    isAccessResolved: defaultFalse(props.isAccessResolved),
    canEdit,
    canManageReservation: defaultFalse(props.canManageReservation),
    activeAcceptedVersionId: props.activeAcceptedVersionId ?? null,
    activeAcceptedVersionNumber: props.activeAcceptedVersionNumber ?? null,
  };
}

export function WorkspacePendingState() {
  const { t } = useTranslation('offers');

  return <div className="p-6 text-sm text-muted-foreground">{t('workspace.loading')}</div>;
}

export function WorkspaceErrorState() {
  const { t } = useTranslation('offers');

  return (
    <div className="p-6 text-sm text-destructive" data-testid="offer-workspace-error">
      {t('workspace.error')}
    </div>
  );
}

export function WorkspaceShell(props: WorkspaceShellProps) {
  const { t } = useTranslation('offers');
  const offerHeaderRef = React.useRef<OfferHeaderHandle>(null);
  const navigate = useNavigate();

  if (props.isForbidden) {
    return <WorkspaceForbiddenState />;
  }

  // Read-only when trashed OR when the offer has progressed beyond DRAFT
  const isTrashed = props.offer.trashedAt != null;
  const isReadOnly = isOfferReadOnly(props.offer);
  const canEdit = props.canEdit ?? !isReadOnly;
  const headerProps = getWorkspaceHeaderProps(props, canEdit, t);
  const finalizationProps = getFinalizationSectionProps(props, canEdit);

  const offerId = String(props.offer.id);
  function handleOpenVersion(versionId: number) {
    void navigate({
      to: '/offers/$offerId/versions/$versionId',
      params: { offerId, versionId: String(versionId) },
    });
  }

  return (
    <div data-testid="offer-workspace-shell">
      {isTrashed ? (
        <div
          className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-900"
          data-testid="offer-workspace-trash-banner"
        >
          {t('workspace.inTrash')}
        </div>
      ) : null}

      <OfferHeader
        ref={offerHeaderRef}
        canAdminChangeStatus={headerProps.canAdminChangeStatus}
        canEdit={headerProps.canEdit}
        canManagePublicLink={headerProps.canManagePublicLink}
        canManageSharing={headerProps.canManageSharing}
        canTrash={headerProps.canTrash}
        clientBrands={headerProps.clientBrands}
        clientName={headerProps.clientName}
        isReadOnly={isReadOnly}
        offer={props.offer}
      />

      <OfferFinalizationSection
        canConfirmCampaign={finalizationProps.canConfirmCampaign}
        canRecordSale={finalizationProps.canRecordSale}
        hasUnsecuredUnits={finalizationProps.hasUnsecuredUnits}
        isAccessResolved={finalizationProps.isAccessResolved}
        canEdit={finalizationProps.canEdit}
        canManageReservation={finalizationProps.canManageReservation}
        activeAcceptedVersionId={finalizationProps.activeAcceptedVersionId}
        activeAcceptedVersionNumber={finalizationProps.activeAcceptedVersionNumber}
        offer={props.offer}
      />

      {/* The offer title/badge/client/date block above scrolls away with the
          page (it's plain document flow, no bounding box). Only the tabs bar
          itself is sticky here (pinned to the top of the `<main>` scroll
          container, app-shell.tsx) — each tab's own toolbar/search/table then
          stays in normal flow (see units-tab-page.tsx etc.) so the whole page
          is a single scroll owner: scrolling with the pointer over the table
          scrolls the page, not a nested box. `h-11`/`top-11` here is the
          shared offset every per-tab sticky toolbar pins itself below. */}
      <Tabs
        value={props.activeTab}
        onValueChange={(value) => props.onTabChange?.(value as OfferWorkspaceTab)}
      >
        <div className="sticky top-0 z-20 flex h-11 items-end border-b border-border bg-background px-6">
          <TabsList className="-mb-px border-b-0">
            {OFFER_WORKSPACE_TABS.map((tab) => (
              <TabsTrigger key={tab} value={tab} data-testid={getWorkspaceTabTriggerTestId(tab)}>
                {getTabLabel(tab, t)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {OFFER_WORKSPACE_TABS.map((tab) => (
          <TabsContent key={tab} value={tab} data-testid={getWorkspaceTabTestId(tab)}>
            {renderWorkspaceTabContent({
              activeTab: tab,
              canEdit,
              offer: props.offer,
              offerHeaderRef,
              onOpenVersion: handleOpenVersion,
              props,
            })}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
